/**
 * YouTubeAdapter — usa APENAS a YouTube IFrame Player API oficial.
 *
 * Não há download, scraping ou extração de áudio do YouTube. O vídeo é:
 *   PLAYER + REFERÊNCIA TEMPORAL + FONTE DE SINCRONIZAÇÃO.
 * Por isso, no modo YouTube o download é do VOCAL (e a mix completa exige um
 * instrumental autorizado enviado pelo usuário).
 */
import type { LoadOptions, PlayerAdapter, PlayerAdapterEvents, PlayerState } from './types.js';

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const API_SRC = 'https://www.youtube.com/iframe_api';
const LOAD_TIMEOUT_MS = 12000;

let apiPromise: Promise<any> | null = null;

function loadYouTubeApi(timeoutMs = LOAD_TIMEOUT_MS): Promise<any> {
  if (typeof window === 'undefined') return Promise.reject(new Error('Sem window'));
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise<any>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('A API do YouTube não respondeu (rede bloqueada ou offline).'));
    }, timeoutMs);

    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      clearTimeout(timer);
      if (prev) prev();
      if (window.YT?.Player) resolve(window.YT);
      else reject(new Error('API do YouTube carregou sem YT.Player.'));
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${API_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.YT?.Player) {
          clearTimeout(timer);
          resolve(window.YT);
        }
      });
      return;
    }
    const tag = document.createElement('script');
    tag.src = API_SRC;
    tag.async = true;
    tag.onerror = () => {
      clearTimeout(timer);
      reject(new Error('Não foi possível carregar o player oficial do YouTube.'));
    };
    document.head.appendChild(tag);
  }).finally(() => {
    // mantém apiPromise para evitar recarregar; em caso de erro permite nova tentativa
    if (!window.YT?.Player) apiPromise = null;
  });
  return apiPromise;
}

export class YouTubeAdapter implements PlayerAdapter {
  readonly kind = 'youtube' as const;
  readonly label = 'YouTube (player oficial)';
  readonly requiresNetwork = true;

  private player: any = null;
  private host: HTMLElement | null = null;
  private events: PlayerAdapterEvents = {};
  private duration = 0;
  private state: PlayerState = 'unstarted';
  private playRequestedAtPerf = 0;
  private lastPollPerf = 0;
  private lastMediaTime = 0;
  private lastKnownState: number | null = null;
  private pollTimer: number | null = null;
  private videoId = '';
  private rate = 1;
  private _lastStartDelayMs = 0;
  private pendingResolve: ((v: { duration: number; title?: string; channel?: string }) => void) | null = null;
  private loadOpts: LoadOptions = {};

  on(events: PlayerAdapterEvents) {
    this.events = { ...this.events, ...events };
  }

  getPlayRequestedAtPerf(): number {
    return this.playRequestedAtPerf;
  }

  async load(host: HTMLElement | null, opts: LoadOptions = {}) {
    this.host = host;
    this.loadOpts = opts;
    const YT = await loadYouTubeApi();
    this.videoId = (host?.dataset.videoId as string) || '';
    if (!this.videoId) throw new Error('VIDEO_ID ausente.');

    const container = host;
    if (!container) throw new Error('Container do player ausente.');
    container.innerHTML = '';
    const mount = document.createElement('div');
    mount.id = `yt-player-${Math.random().toString(36).slice(2, 8)}`;
    mount.style.width = '100%';
    mount.style.height = '100%';
    container.appendChild(mount);

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('O player do YouTube demorou a responder.')), LOAD_TIMEOUT_MS);
      this.player = new YT.Player(mount.id, {
        videoId: this.videoId,
        host: 'https://www.youtube-nocookie.com',
        playerVars: {
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          iv_load_policy: 3,
          origin: typeof location !== 'undefined' ? location.origin : undefined,
        },
        events: {
          onReady: () => {
            clearTimeout(timeout);
            try {
              this.duration = this.player.getDuration?.() || 0;
              const data = this.player.getVideoData?.() || {};
              this.state = 'cued';
              this.events.onReady?.({ duration: this.duration });
              if (this.loadOpts.startSeconds) this.player.seekTo(this.loadOpts.startSeconds, true);
              this.startPolling();
              resolve();
            } catch (err) {
              reject(err as Error);
            }
          },
          onStateChange: (e: any) => this.handleState(e.data),
          onError: (e: any) => {
            clearTimeout(timeout);
            const msg = mapError(e.data);
            this.state = 'unavailable';
            this.events.onError?.(e.data, msg);
            reject(new Error(msg));
          },
        },
      });
    });

    const meta = this.getVideoData();
    return { duration: this.duration, title: meta.title, channel: meta.channel };
  }

  private handleState(ytState: number) {
    this.lastKnownState = ytState;
    const map: Record<number, PlayerState> = {
      [-1]: 'unstarted',
      0: 'ended',
      1: 'playing',
      2: 'paused',
      3: 'buffering',
      5: 'cued',
    };
    this.state = map[ytState] ?? this.state;
    if (ytState === 1) {
      const startedAtPerf = performance.now();
      const delay = this.playRequestedAtPerf ? startedAtPerf - this.playRequestedAtPerf : 0;
      this.lastMediaTime = this.rawCurrentTime();
      this.lastPollPerf = startedAtPerf;
      this.events.onPlaybackStarted?.({
        requestedAtPerf: this.playRequestedAtPerf,
        startedAtPerf,
        mediaTime: this.lastMediaTime,
      });
      if (delay > 0) this._lastStartDelayMs = delay;
    }
    this.events.onStateChange?.(this.state);
  }

  /** Atraso medido entre chamar play() e o player realmente começar (ms). */
  get lastStartDelayMs(): number {
    return this._lastStartDelayMs;
  }

  private rawCurrentTime(): number {
    try {
      const t = this.player?.getCurrentTime?.();
      return typeof t === 'number' && isFinite(t) ? t : this.lastMediaTime;
    } catch {
      return this.lastMediaTime;
    }
  }

  private startPolling() {
    if (this.pollTimer != null) return;
    this.pollTimer = window.setInterval(() => {
      const t = this.rawCurrentTime();
      this.lastMediaTime = t;
      this.lastPollPerf = performance.now();
      if (!this.duration) {
        const d = this.player?.getDuration?.();
        if (d) {
          this.duration = d;
          this.events.onReady?.({ duration: d });
        }
      }
    }, 200);
  }

  /**
   * Tempo atual com interpolação entre polls: menos jitter para a
   * reprodução sincronizada do vocal.
   */
  getCurrentTime(): number {
    if (!this.player) return 0;
    const t = this.rawCurrentTime();
    this.lastMediaTime = t;
    if (this.state === 'playing') {
      const now = performance.now();
      const elapsed = Math.max(0, Math.min(1000, now - this.lastPollPerf)) / 1000;
      const est = t + elapsed * this.rate;
      this.lastPollPerf = now;
      return this.duration ? Math.min(est, this.duration) : est;
    }
    return t;
  }

  getDuration(): number {
    const d = this.player?.getDuration?.();
    if (typeof d === 'number' && d > 0) this.duration = d;
    return this.duration;
  }

  getVideoData(): { title?: string; channel?: string; videoId?: string } {
    try {
      const d = this.player?.getVideoData?.() || {};
      return { title: d.title, channel: d.author, videoId: d.video_id };
    } catch {
      return {};
    }
  }

  getState(): PlayerState {
    return this.state;
  }

  async play(): Promise<void> {
    if (!this.player) throw new Error('Player não carregado.');
    this.playRequestedAtPerf = performance.now();
    this._lastStartDelayMs = 0;
    try {
      this.player.playVideo();
    } catch (err) {
      throw new Error('Não foi possível iniciar o vídeo do YouTube.');
    }
  }

  pause(): void {
    if (this.state === 'playing') this.playRequestedAtPerf = 0;
    try {
      this.player?.pauseVideo();
    } catch {
      /* noop */
    }
  }

  stop(): void {
    try {
      this.player?.stopVideo();
    } catch {
      /* noop */
    }
  }

  seek(sec: number): void {
    try {
      this.player?.seekTo(Math.max(0, sec), true);
      this.lastMediaTime = Math.max(0, sec);
      this.lastPollPerf = performance.now();
    } catch {
      /* noop */
    }
  }

  setVolume(v: number): void {
    try {
      this.player?.setVolume(Math.round(Math.max(0, Math.min(1, v)) * 100));
    } catch {
      /* noop */
    }
  }

  setPlaybackRate(r: number): void {
    try {
      const allowed: number[] = this.player?.getAvailablePlaybackRates?.() ?? [1];
      const nearest = allowed.reduce((best, x) =>
        Math.abs(x - r) < Math.abs(best - r) ? x : best, allowed[0] ?? 1);
      this.player?.setPlaybackRate(nearest);
      this.rate = nearest;
    } catch {
      /* noop */
    }
  }

  getPlaybackRate(): number {
    try {
      const r = this.player?.getPlaybackRate?.();
      if (typeof r === 'number' && r > 0) this.rate = r;
    } catch {
      /* noop */
    }
    return this.rate;
  }

  destroy(): void {
    if (this.pollTimer != null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    try {
      this.player?.destroy?.();
    } catch {
      /* noop */
    }
    this.player = null;
    if (this.host) this.host.innerHTML = '';
  }
}

function mapError(code: number): string {
  switch (code) {
    case 2:
      return 'O ID do vídeo é inválido. Confira o link.';
    case 5:
      return 'Este vídeo não pode ser reproduzido em player HTML5.';
    case 100:
      return 'Vídeo não encontrado (pode ter sido removido ou é privado).';
    case 101:
    case 150:
      return 'O dono do vídeo não permite reprodução fora do YouTube.';
    default:
      return `O YouTube retornou um erro (código ${code}).`;
  }
}
